import { type Chat, ChatMessages, Features, RecordingStatus, timestampToDate } from "@storytell/cp-client";
import { dispatchGlobalEvent, isNetworkError } from "@storytell/events";
import { getTextFromDoc } from "@storytell/rich-text/src/converters/getTextFromDoc";
import { richTextToProseMirror } from "@storytell/rich-text/src/converters/richTextToProseMirror";
import type { UserId } from "@storytell/types";
import { batch } from "solid-js";
import { produce } from "solid-js/store";
import { clearAllGranularStores } from "../../granularStores";
import {
  createEmptyStreamingChat,
  type SetStreamingStore,
  type StreamingBlockId,
  type StreamingChatId,
  type StreamingMessage,
  type StreamingMessageId,
  StreamingMessageStatus,
  type StreamingOperationId,
  type StreamingPrompt,
  type StreamingStore,
} from "../streamingTypes";
import { processBlockDelta, processBlockStart, processBlockStop } from "./streamingBlockHandlers";
import {
  type PendingTurn,
  processMessageError,
  processMessageRetry,
  processMessageStart,
  processMessageStop,
} from "./streamingMessageHandlers";
import {
  processOperationComplete,
  processOperationFailed,
  processOperationProgress,
  processOperationStart,
} from "./streamingOperationHandlers";

/**
 * Reads the live stream of a turn the caller has just requested. Exactly one of the callbacks
 * below ends it, unless abortSignal fires first: onMessageFinished or onMessageFailed when the turn
 * ends, onStreamInterrupted when the stream stops delivering after MESSAGE_START but before that,
 * and onStreamError when it fails before the turn started.
 */
export const startStreamingChat = async ({
  store,
  setStore,
  stream,
  onChatIdCaptured,
  onMessageStarted,
  onMessageFinished,
  onMessageFailed,
  onStreamError,
  onStreamInterrupted,
  request,
  abortSignal,
}: {
  store: StreamingStore;
  setStore: SetStreamingStore;
  /** The response stream for `request`, opened with `abortSignal`. */
  stream: AsyncIterable<Chat.StreamResponse>;
  /**
   * The chat ID is sent as part of the message start event
   * Needs a callback so the app can handle it by updating the URL
   */
  onChatIdCaptured?: (chatId: StreamingChatId) => void;
  onMessageStarted?: (messageId: StreamingMessageId) => void;
  onMessageFinished?: (messageId: StreamingMessageId) => void;
  onMessageFailed?: (messageId: StreamingMessageId) => void;
  onStreamError?: (error: unknown) => void;
  /** The turn may still be running server-side; only playback can tell. */
  onStreamInterrupted?: () => void;
  request: Chat.StreamRequest;
  abortSignal?: AbortSignal;
}): Promise<void> => {
  const submittedAt = new Date();
  const parentMessageId = request.prompt?.parentMessageId as StreamingMessageId | undefined;
  const pendingTurn = describePendingTurn(request);
  let started = false;

  let outcome: Awaited<ReturnType<typeof processStreamingChat>>;
  try {
    outcome = await processStreamingChat(
      store,
      setStore,
      stream,
      onChatIdCaptured,
      (messageId) => {
        started = true;
        onMessageStarted?.(messageId);
      },
      onMessageFinished,
      onMessageFailed,
      abortSignal,
      submittedAt,
      parentMessageId,
      pendingTurn
    );
  } catch (error) {
    if (!abortSignal?.aborted) onStreamError?.(error);
    return;
  }
  if (outcome === "completed" || abortSignal?.aborted) return;
  if (started) {
    onStreamInterrupted?.();
    return;
  }
  // Nothing to resume without a message id, and nothing would ever end the turn otherwise.
  onStreamError?.(new Error("The chat stream closed before the turn started."));
};

const describePendingTurn = (request: Chat.StreamRequest): PendingTurn | undefined => {
  const prompt = request.prompt;
  const chatId = prompt?.chatId as StreamingChatId | undefined;
  if (!prompt || !chatId) return undefined;

  const promptRichText = prompt.prompt.case === "richText" ? richTextToProseMirror(prompt.prompt.value) : undefined;
  return {
    chatId,
    parentMessageId: prompt.parentMessageId ? (prompt.parentMessageId as StreamingMessageId) : undefined,
    promptText:
      prompt.prompt.case === "text" ? prompt.prompt.value : promptRichText ? getTextFromDoc(promptRichText) : "",
    promptRichText,
  };
};

type StreamOutcome = "completed" | "interrupted";

/**
 * Routes each event of a chat stream to its handler. Resolves "completed" once every message the
 * stream started has reached its terminal event (MESSAGE_STOP, or a MESSAGE_ERROR that ends the
 * turn), and "interrupted" when the stream closed or failed short of that. Rejects only when it
 * fails before any message started, which is about the request rather than the turn, and never
 * after abortSignal fires.
 */
export const processStreamingChat = async (
  store: StreamingStore,
  setStore: SetStreamingStore,
  stream: AsyncIterable<Chat.StreamResponse> | AsyncIterable<Chat.StreamPlaybackResponse>,
  onChatIdCaptured?: (chatId: StreamingChatId) => void,
  onMessageStarted?: (messageId: StreamingMessageId) => void,
  onMessageFinished?: (messageId: StreamingMessageId) => void,
  onMessageFailed?: (messageId: StreamingMessageId) => void,
  abortSignal?: AbortSignal,
  submittedAt?: Date,
  parentMessageId?: StreamingMessageId,
  pendingTurn?: PendingTurn
): Promise<StreamOutcome> => {
  const open = new Set<StreamingMessageId>();
  const failedOpen = new Set<StreamingMessageId>();
  let ended = false;
  const outcome = (): StreamOutcome => (ended && open.size === 0 ? "completed" : "interrupted");
  const started = (messageId: StreamingMessageId) => {
    open.add(messageId);
    onMessageStarted?.(messageId);
  };
  const finished = (messageId: StreamingMessageId) => {
    open.delete(messageId);
    ended = true;
    if (failedOpen.delete(messageId)) onMessageFailed?.(messageId);
    else onMessageFinished?.(messageId);
  };
  // A fatal error on a started turn doesn't end it: a failed tool keeps the turn running, and the
  // server closes every turn with MESSAGE_STOP. Until that lands the stream is still live.
  const failed = (messageId: StreamingMessageId) => {
    if (open.has(messageId)) {
      failedOpen.add(messageId);
      return;
    }
    ended = true;
    onMessageFailed?.(messageId);
  };

  try {
    for await (const response of stream) {
      if (abortSignal?.aborted) {
        return outcome();
      }

      batch(() => {
        for (const message of response.messages) {
          processResponseMessage(
            store,
            setStore,
            message,
            onChatIdCaptured,
            started,
            finished,
            failed,
            /* eventTimestamp */ undefined,
            /* overrideChatId */ undefined,
            /* sourceChatId */ undefined,
            /* sourceMessageId */ undefined,
            submittedAt,
            parentMessageId,
            pendingTurn
          );
        }
      });
    }
  } catch (error) {
    if (abortSignal?.aborted) {
      return outcome();
    }
    if (isNetworkError(error)) {
      dispatchGlobalEvent("connectivity-streaming-error", {
        error: String(error),
        code: undefined,
      });
    }
    // Once a message has started, the error only says the connection broke, and each browser
    // words that differently; whether the turn survived is for playback to find out.
    if (open.size > 0 || ended) {
      return outcome();
    }
    throw error;
  }
  return outcome();
};

export const processChatRecording = (
  store: StreamingStore,
  setStore: SetStreamingStore,
  recording: Chat.ConversationResponse,
  activeMessageId?: string
) => {
  // Clear accumulated content from previous visits to prevent duplication
  clearAllGranularStores();

  const chatId = recording.chatId as StreamingChatId;
  const recordings = recording.messages;

  batch(() => {
    // Ensure the chat exists in the store even if there are no messages yet
    // This handles the case of a brand new chat that's still streaming
    setStore(
      produce((draft) => {
        if (!draft.chats[chatId]) {
          draft.chats[chatId] = createEmptyStreamingChat(chatId);
        }
        draft.chats[chatId].hasBranches = recording.hasBranches;
      })
    );

    for (let recordingIndex = 0; recordingIndex < recordings.length; recordingIndex++) {
      const rec = recordings[recordingIndex];
      if (!rec) continue;
      if (rec.status === RecordingStatus.LOAD_FAILED) {
        const errorMessage = loadFailedMessage(chatId, rec, recordingIndex);

        // Listed so the turn renders its load-error state: a chat whose every recording failed
        // would otherwise open empty, as if its history were gone.
        setStore(
          produce((draft) => {
            if (!draft.chats[chatId]) {
              draft.chats[chatId] = createEmptyStreamingChat(chatId);
            }
            draft.messages[errorMessage.id] = errorMessage;
            if (!draft.chats[chatId].messages.includes(errorMessage.id)) {
              const messages = [...draft.chats[chatId].messages, errorMessage.id];
              messages.sort((a, b) => (draft.messages[a]?.index || 0) - (draft.messages[b]?.index || 0));
              draft.chats[chatId].messages = messages;
            }
          })
        );

        continue;
      }

      // Handle recordings with empty messages (e.g., recently interrupted messages still being written)
      if (rec.messages.length === 0) {
        // The server always emits a recording for the active message but never populates it, so
        // leave the slot untouched for the playback stream. Building anything here writes a
        // COMPLETED message the streaming state would then have to argue with.
        if (rec.messageId === activeMessageId) continue;

        const messageId = rec.messageId as StreamingMessageId;

        const pendingMessage: StreamingMessage = {
          id: messageId,
          chatId,
          feature: Features.Feature.UNSPECIFIED,
          index: recordingIndex,
          role: ChatMessages.Role.ASSISTANT,
          prompt: {
            messageId,
            blockId: `block_${messageId}_prompt` as StreamingBlockId,
            operationId: `operation_${messageId}_prompt` as StreamingOperationId,
            text: "",
            scope: undefined,
          } satisfies StreamingPrompt,
          operations: [],
          status: StreamingMessageStatus.FAILED,
          aggregateMetrics: {
            ingressTokens: 0,
            egressTokens: 0,
            ingressBytes: 0,
            egressBytes: 0,
            totalCredits: 0,
            ingressCredits: 0,
            egressCredits: 0,
          },
          createdBy: "" as UserId,
          sourceChatId: rec.sourceChatId as StreamingChatId | undefined,
          sourceMessageId: rec.sourceMessageId as StreamingMessageId | undefined,
          parentMessageId: rec.parentMessageId ? (rec.parentMessageId as StreamingMessageId) : undefined,
          loadError: false,
        };

        setStore(
          produce((draft) => {
            if (!draft.chats[chatId]) {
              draft.chats[chatId] = createEmptyStreamingChat(chatId);
            }
            draft.messages[messageId] = pendingMessage;
            // Do not add to chat message list so empty placeholders do not appear as "Someone else" in the thread after reload
          })
        );

        continue;
      }

      applyPopulatedRecording(store, setStore, chatId, rec, recordingIndex, true);
    }

    // Fallback: if USER messages still have no createdBy after replay, fill from chat audit metadata
    // (legacy or edge cases). Prefer MESSAGE_START from the recording when it is present.
    const chatCreator = recording.auditMetadata?.created;
    if (chatCreator?.userId) {
      setStore(
        produce((draft) => {
          const ids = draft.chats[chatId]?.messages ?? [];
          for (const mid of ids) {
            const m = draft.messages[mid];
            if (!m || m.role !== ChatMessages.Role.USER) continue;
            if (m.createdBy && m.createdBy.trim() !== "") continue;
            m.createdBy = chatCreator.userId as UserId;
            const email = chatCreator.userEmail?.trim();
            if (email) m.createdByEmail = email;
          }
        })
      );
    }
  });
};

type Recording = Chat.ConversationResponse["messages"][number];

// A recording the server couldn't read carries only ids, so there is no prompt or answer to fill in.
const loadFailedMessage = (chatId: StreamingChatId, rec: Recording, index: number): StreamingMessage => {
  const messageId = rec.messageId as StreamingMessageId;
  return {
    id: messageId,
    chatId,
    feature: Features.Feature.UNSPECIFIED,
    index,
    role: ChatMessages.Role.ASSISTANT,
    prompt: {
      messageId,
      blockId: `block_${messageId}_prompt` as StreamingBlockId,
      operationId: `operation_${messageId}_prompt` as StreamingOperationId,
      text: "",
      scope: undefined,
    } satisfies StreamingPrompt,
    operations: [],
    status: StreamingMessageStatus.FAILED,
    aggregateMetrics: {
      ingressTokens: 0,
      egressTokens: 0,
      ingressBytes: 0,
      egressBytes: 0,
      totalCredits: 0,
      ingressCredits: 0,
      egressCredits: 0,
    },
    createdBy: "" as UserId,
    sourceChatId: rec.sourceChatId as StreamingChatId | undefined,
    sourceMessageId: rec.sourceMessageId as StreamingMessageId | undefined,
    parentMessageId: rec.parentMessageId ? (rec.parentMessageId as StreamingMessageId) : undefined,
    loadError: true,
  };
};

/**
 * Builds the StreamingMessage for a recording with events and replays them into the
 * granular stores. No-op if the message already exists. With insertIntoList, the message
 * id is appended to the chat's list and kept ordered by `index`; pass false when the caller
 * places the id in the list itself.
 */
const applyPopulatedRecording = (
  store: StreamingStore,
  setStore: SetStreamingStore,
  chatId: StreamingChatId,
  rec: Recording,
  index: number,
  insertIntoList: boolean
) => {
  // Persisted recordings drop pings and system operation heartbeats from the
  // transmitted stream (ENG-5450), but keep MESSAGE_START so createdBy still populates.
  const messageId = rec.messageId as StreamingMessageId;
  // When the turn started, as the recording remembers it. MESSAGE_START is the event that
  // carries it; the earliest event standing in for it keeps a recording that somehow lacks
  // one from reading as a turn that was never recorded at all, which sorts newest.
  const recordedStart =
    rec.messages.find((m) => m.message?.event === ChatMessages.Event.MESSAGE_START) ?? rec.messages[0];
  const recordedStartedAt = recordedStart?.timestamp ? timestampToDate(recordedStart.timestamp) : undefined;
  setStore(
    produce((draft) => {
      if (!draft.chats[chatId]) {
        draft.chats[chatId] = createEmptyStreamingChat(chatId);
      }

      if (!draft.messages[messageId]) {
        const firstEvent = rec.messages[0]?.message;
        const role = firstEvent?.role || ChatMessages.Role.ASSISTANT;

        const streamingMessage: StreamingMessage = {
          id: messageId,
          chatId,
          feature: Features.Feature.UNSPECIFIED,
          index,
          role,
          prompt: {
            messageId,
            blockId: `block_${messageId}_prompt` as StreamingBlockId,
            operationId: `operation_${messageId}_prompt` as StreamingOperationId,
            text: "",
            scope: undefined,
          } satisfies StreamingPrompt,
          operations: [],
          status: StreamingMessageStatus.COMPLETED,
          aggregateMetrics: {
            ingressTokens: 0,
            egressTokens: 0,
            ingressBytes: 0,
            egressBytes: 0,
            totalCredits: 0,
            ingressCredits: 0,
            egressCredits: 0,
          },
          createdBy: "" as UserId,
          submittedAt: recordedStartedAt,
          sourceChatId: rec.sourceChatId as StreamingChatId | undefined,
          sourceMessageId: rec.sourceMessageId as StreamingMessageId | undefined,
          parentMessageId: rec.parentMessageId ? (rec.parentMessageId as StreamingMessageId) : undefined,
        };

        draft.messages[messageId] = streamingMessage;

        if (insertIntoList && !draft.chats[chatId].messages.includes(messageId)) {
          const newMessages = [...draft.chats[chatId].messages, messageId];
          newMessages.sort((a, b) => (draft.messages[a]?.index || 0) - (draft.messages[b]?.index || 0));
          draft.chats[chatId].messages = newMessages;
        }

        // Link operations that arrived before this message was created.
        const orphanedOps = draft.orphanedOperations[messageId];
        if (orphanedOps) {
          for (const opId of orphanedOps) {
            if (!streamingMessage.operations.includes(opId)) {
              streamingMessage.operations.push(opId);
            }
          }
          delete draft.orphanedOperations[messageId];
        }
      }
    })
  );

  for (const message of rec.messages) {
    const eventTimestamp = message.timestamp ? timestampToDate(message.timestamp) : undefined;
    // A live turn takes its submitted time from the client clock at send. On replay the
    // recorded MESSAGE_START timestamp is the same instant, and it is the only one the
    // turn's elapsed time and its sibling ordering can be rebuilt from.
    const submittedAt = message.message?.event === ChatMessages.Event.MESSAGE_START ? eventTimestamp : undefined;
    // Forked recordings carry the original message/chat IDs in their inner events.
    // Override with the forked IDs so operations and blocks link correctly and
    // MESSAGE_START updates the pre-created message instead of creating a duplicate.
    const eventMessage = { ...message.message, id: messageId };
    processResponseMessage(
      store,
      setStore,
      eventMessage,
      undefined,
      undefined,
      undefined,
      undefined,
      eventTimestamp,
      chatId,
      rec.sourceChatId as StreamingChatId | undefined,
      rec.sourceMessageId as StreamingMessageId | undefined,
      submittedAt,
      rec.parentMessageId ? (rec.parentMessageId as StreamingMessageId) : undefined
    );
  }
};

/**
 * Merges older paginated recordings into a loaded chat. Does not clear the granular
 * stores, so already-rendered messages keep their accumulated block content; messages
 * already in the store are skipped because re-replaying their events would duplicate it.
 *
 * Returns the number of messages newly added, which the caller uses to detect end of history.
 */
export const mergeOlderRecordings = (
  store: StreamingStore,
  setStore: SetStreamingStore,
  recording: Chat.ConversationResponse
): number => {
  const chatId = recording.chatId as StreamingChatId;
  const recordings = recording.messages;
  // The message index from recorded events is unreliable for ordering (event replay
  // overwrites it), so don't sort by it. These messages are all older than everything
  // loaded, so collect the new ids in server order and prepend them as a block.
  const olderIds: StreamingMessageId[] = [];
  const loadedIds = new Set(store.chats[chatId]?.messages);

  batch(() => {
    for (let recordingIndex = 0; recordingIndex < recordings.length; recordingIndex++) {
      const rec = recordings[recordingIndex];
      if (!rec) continue;
      const loadFailed = rec.status === RecordingStatus.LOAD_FAILED;
      if (rec.messages.length === 0 && !loadFailed) continue;

      const messageId = rec.messageId as StreamingMessageId;
      // List membership is the real "already loaded" signal: the initial load writes
      // empty-stub placeholders into store.messages for out-of-window messages without
      // listing them, and those must be replaced with content here, not skipped.
      if (loadedIds.has(messageId)) continue;

      // Counted as added, or a page of failed recordings would read as the start of history.
      if (loadFailed) {
        setStore(
          produce((s) => {
            s.messages[messageId] = loadFailedMessage(chatId, rec, recordingIndex);
          })
        );
        olderIds.push(messageId);
        continue;
      }

      // applyPopulatedRecording's build guard skips anything already in store.messages,
      // so drop a stale placeholder first to let the message rebuild with its content.
      if (store.messages[messageId]) {
        setStore(
          produce((s) => {
            delete s.messages[messageId];
          })
        );
      }

      applyPopulatedRecording(store, setStore, chatId, rec, recordingIndex, false);
      olderIds.push(messageId);
    }

    if (olderIds.length > 0) {
      setStore(
        produce((s) => {
          const chat = s.chats[chatId];
          if (!chat) return;
          const olderSet = new Set<string>(olderIds);
          const rest = chat.messages.filter((id) => !olderSet.has(id));
          chat.messages = [...olderIds, ...rest];
        })
      );
    }
  });

  return olderIds.length;
};

const processResponseMessage = (
  store: StreamingStore,
  setStore: SetStreamingStore,
  message: ChatMessages.Message,
  onChatIdCaptured?: (chatId: StreamingChatId) => void,
  onMessageStarted?: (messageId: StreamingMessageId) => void,
  onMessageFinished?: (messageId: StreamingMessageId) => void,
  onMessageFailed?: (messageId: StreamingMessageId) => void,
  eventTimestamp?: Date,
  overrideChatId?: StreamingChatId,
  sourceChatId?: StreamingChatId,
  sourceMessageId?: StreamingMessageId,
  submittedAt?: Date,
  parentMessageId?: StreamingMessageId,
  pendingTurn?: PendingTurn
) => {
  switch (message.event) {
    case ChatMessages.Event.MESSAGE_ERROR:
      processMessageError(setStore, message, onMessageFailed, pendingTurn);
      return;
    case ChatMessages.Event.MESSAGE_RETRY:
      processMessageRetry(setStore, message);
      return;
    case ChatMessages.Event.MESSAGE:
      return;
    case ChatMessages.Event.MESSAGE_START:
      processMessageStart(
        setStore,
        message,
        onChatIdCaptured,
        onMessageStarted,
        overrideChatId,
        sourceChatId,
        sourceMessageId,
        submittedAt,
        parentMessageId
      );
      return;
    case ChatMessages.Event.MESSAGE_STOP:
      processMessageStop(setStore, message, eventTimestamp);
      onMessageFinished?.(message.id as StreamingMessageId);
      return;
    case ChatMessages.Event.OPERATION_START:
      processOperationStart(setStore, message);
      return;
    case ChatMessages.Event.OPERATION_PROGRESS:
      processOperationProgress(setStore, message);
      return;
    case ChatMessages.Event.OPERATION_COMPLETE:
      processOperationComplete(setStore, message);
      return;
    case ChatMessages.Event.MESSAGE_BLOCK_START:
    case ChatMessages.Event.MESSAGE_BLOCK_GENERATION_START:
      processBlockStart(setStore, message, eventTimestamp);
      return;
    case ChatMessages.Event.MESSAGE_DELTA:
    case ChatMessages.Event.MESSAGE_BLOCK_DELTA:
    case ChatMessages.Event.MESSAGE_BLOCK_GENERATION_DELTA:
      processBlockDelta(store, setStore, message);
      return;
    case ChatMessages.Event.MESSAGE_BLOCK_STOP:
    case ChatMessages.Event.MESSAGE_BLOCK_GENERATION_STOP:
      processBlockStop(setStore, message, eventTimestamp);
      return;
    case ChatMessages.Event.OPERATION_FAILED:
      processOperationFailed(setStore, message);
      return;
    case ChatMessages.Event.UNSPECIFIED:
    case ChatMessages.Event.MESSAGE_PING:
    case ChatMessages.Event.MESSAGE_WARNING:
    case ChatMessages.Event.MESSAGE_LIMIT:
    case ChatMessages.Event.MESSAGE_CANCEL:
    case ChatMessages.Event.PROMPT:
    case ChatMessages.Event.ACTION_TAKEN:
    case ChatMessages.Event.BLOCK:
    case ChatMessages.Event.OPERATION_CANCELLED:
    case ChatMessages.Event.INSIGHT:
      return;
  }
};
